<script>
  import mime from "mime";
  import { downloadBlob } from "downloaders";

  import { Sentry } from "#src/glitchtip.js";

  import UploadIcon from "@local/assets/icons/upload.svg";
  import BlankIcon from "@local/assets/icons/blank.svg";
  import TrashIcon from "@local/assets/icons/trash.svg";
  import CaretLeft from "@local/assets/icons/caret-left.svg";
  import CaretRight from "@local/assets/icons/caret-right.svg";
  import FileIcon from "@local/assets/icons/file.svg";
  import FilePdfIcon from "@local/assets/icons/file-pdf.svg";
  import FileImgIcon from "@local/assets/icons/file-img.svg";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import EditIcon from "@local/assets/icons/edit.svg";
  import ImageIcon from "@local/assets/icons/image.svg";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import LocationItemIcon from "@local/assets/icons/location-item-empty.svg";
  import ShareIcon from "@local/assets/icons/share.svg";

  import { Modal } from "svelte-utilities";
  import { api, storeObject } from "#src/api";
  import { profile } from "#src/stores/auth.js";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import Dropzone from "#lib/dropzone/Dropzone.svelte";
  import decodeImage from "#src/extensions/dom/image-decode.js";
  import imgClipToBase64 from "#src/extensions/dom/img-clip-to-base64.js";
  import imgClipToBlob from "#src/extensions/dom/img-clip-to-blob.js";
  import extension from "@local/extensions/utilities/extension.js";
  import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
  import { createClipAttachment, createIIIFDrawing } from "@local/lamina-core";
  import { tooltip } from "svelte-utilities";
  import { SUPABASE_URL as supabaseUrl } from "#src/env";
  import { settingTriopsUrl as triopsUrl } from "#src/stores/ui.js";

  export let attachments = [];
  export let group;
  export let itemid = null;
  export let typeid = null;
  export let disabled = false;

  const fileIcons = {
    file: BlankIcon,
    pdf: FilePdfIcon,
    jpg: FileImgIcon,
    jpeg: FileImgIcon,
    png: FileImgIcon,
    svg: FileImgIcon,
  };

  const previewable = ["image/jpeg", "image/png", "image/tiff", "image/gif", "application/pdf"];

  let dropzoneModal;
  let dropzone;
  let attachmentToRemove;
  let removeModal;
  let updateLinkModal;

  let currentImageIndex = 0;
  let thumbnail;

  let uploadUrl = false;
  let attachmentToUpdate = null;
  let urlName = "URL Attachment";
  let urlToUpload = "";

  $: clips = getClips($group);
  $: imageAttachments = attachments.filter((a) => previewable.includes(a.metadata.content_type));
  $: viewableImages = [...clips, ...imageAttachments];
  $: attachmentList = [...clips, ...attachments];
  $: currentImage = viewableImages[currentImageIndex];
  $: getImageThumbnail(currentImage);
  $: prevImage = viewableImages[currentImageIndex - 1];
  $: nextImage = viewableImages[currentImageIndex + 1];

  function upload() {
    urlName = "URL Attachment";
    urlToUpload = "";
    dropzoneModal.open();
  }

  function name(filename) {
    const segs = filename.split(".");
    if (segs.length > 1) return segs.slice(0, -1).join(".");
    return segs[0];
  }

  function filename(path) {
    return path.split("/").pop();
  }

  function iiifName(attachment) {
    return `${nameMinusExtension(attachment.object_id)}.0.tiff`;
  }

  function getClips(group) {
    const recordid = itemid || typeid;
    if (!recordid) return [];

    return group.locations.order
      .filter((id) => {
        const loc = group.locations[id];
        return (
          loc?.record_id === recordid &&
          loc.clip &&
          loc.page_id &&
          group.documents[loc.document_id]?.data.pages?.[loc.page_id]
        );
      })
      .map((id) => {
        if (!id) return null;
        const location = group.locations[id];
        return createClipAttachment(group, location);
      });
  }

  async function uploadFiles(evt) {
    const { file, data } = evt.detail;
    const id = crypto.randomUUID();
    const ext = extension(file.name);
    let fname = file.name;
    while (attachments.some((a) => filename(a.name) === fname)) {
      const n = name(fname);
      fname = n + "-copy." + ext;
    }

    const object_id = `${id}/doc.${ext}`;
    const contentType = mime.getType(ext);

    try {
      const s = await storeObject($group.id, "attachments", object_id, contentType, data);

      await group.addAttachment({
        id,
        user_id: $profile.id,
        group_id: $group.id,
        item_id: itemid,
        type_id: typeid,
        object_id,
        preview_object_id: s.sidecarPath || object_id,
        name: fname,
        metadata: {
          id,
          extension: ext,
          content_type: contentType,
        },
      });
    } catch (err) {
      Sentry.captureException(err);
    }

    dropzone.stopLoading();
    dropzoneModal.close();
  }

  async function uploadUrlAttachment() {
    const id = crypto.randomUUID();
    await group.addAttachment({
      id,
      user_id: $profile.id,
      group_id: $group.id,
      item_id: itemid,
      type_id: typeid,
      url: urlToUpload,
      name: urlName,
      metadata: {
        id,
        content_type: "text/uri-list",
      },
    });

    urlName = "URL Attachment";
    urlToUpload = "";
    dropzoneModal.close();
  }

  async function waitUntil(condition) {
    return await new Promise((resolve) => {
      const interval = setInterval(async () => {
        const ready = await condition();
        if (ready) {
          resolve(ready);
          clearInterval(interval);
        }
      }, 1000);
    });
  }

  async function pdfThumbnail(attachment) {
    if (attachment.preview_object_id) {
      const url = await api.storage.from("attachments").createSignedUrl(attachment.preview_object_id, 60, {
        transform: { width: 540, height: 320, resize: "contain" },
      });

      if (!url.error) {
        return url.data.signedUrl;
      }
    }

    const iiif0name = iiifName(attachment);
    const { data: exists, error } = await api.rpc("storage_object_exists", {
      bucket_id: "drawings",
      object_id: iiif0name,
    });

    if (error) throw error;

    if (!exists) {
      const condition = async () => {
        const { data: exists } = await api.rpc("storage_object_exists", {
          bucket_id: "drawings",
          object_id: iiif0name,
        });
        if (exists) return true;
        return false;
      };

      await waitUntil(condition);
    }

    const path = encodeURIComponent(iiif0name);
    return `${$triopsUrl}/iiif/2/drawings/${path}/full/540,/0/default.jpg`;
  }

  async function ensureDrawingExists(attachment) {
    if (attachment.type === "clip") {
      if (attachment.extension === "pdf") {
        return createIIIFDrawing(attachment);
      } else {
        const file = await api.storage.from("documents").download(attachment.object_name);
        if (file.error) throw file.error;

        const url = URL.createObjectURL(file.data);
        const blob = await imgClipToBlob(url, attachment.location.clip);
        const clipId = crypto.randomUUID();
        const object_id = `${attachment.object_name}/${clipId}.png`;

        const stored = await api.storage.from("drawings").upload(object_id, blob, {
          contentType: "image/png",
        });
        if (stored.error) throw stored.error;

        return {
          attachment_id: attachment.id,
          id: clipId,
          name: attachment.name,
          type: "image",
          extension: "png",
          object_id,
          preview_object_id: object_id,
          properties: {
            width: attachment.location.clip.width,
            height: attachment.location.clip.height,
          },
        };
      }
    } else if (attachment.metadata.extension === "pdf") {
      const id = attachment.metadata.id;

      // First, check if the attachment already has a viewable image in the "drawings" bucket.
      const { data: exists } = await api.rpc("storage_object_exists", {
        bucket_id: "drawings",
        object_id: `${id}/doc.pdf.tiff`,
      });

      if (exists) {
        const path = encodeURIComponent(`${id}/doc.pdf.tiff`);
        const dwgurl = `${supabaseUrl}/storage/v1/render/image/public/drawings/${path}`;
        const dwgimg = await decodeImage(dwgurl);

        return {
          attachment_id: attachment.id,
          id,
          name: attachment.name,
          type: "image",
          extension: "jpg",
          object_id: `${id}/doc.pdf`,
          preview_object_id: `${id}/doc.pdf.tiff`,
          properties: {
            width: dwgimg.width,
            height: dwgimg.height,
          },
        };
      }

      // If not, we will store the object in the "drawings" bucket and create a thumbnail.
      const original = `${id}/doc.pdf`;
      const contentType = "application/pdf";
      const { data, error } = await api.storage.from("attachments").download(attachment.object_id);
      if (error) throw error;
      const s = await storeObject($group.id, "drawings", original, contentType, data);

      return {
        attachment_id: attachment.id,
        id,
        name: attachment.name,
        type: "image",
        extension: "jpg",
        object_id: original,
        preview_object_id: s.sidecarPath,
        properties: s.sidecarProperties,
      };
    } else {
      // If the attachment is an image, we can first check if it is present in the "drawings"
      // bucket. If not, we can store it there.

      const { data: exists } = await api.rpc("storage_object_exists", {
        bucket_id: "drawings",
        object_id: attachment.object_id,
      });

      if (!exists) {
        const a = await api.storage.from("attachments").download(attachment.object_id);
        if (a.error) throw a.error;
        await api.storage.from("drawings").upload(attachment.object_id, a.data, {
          contentType: attachment.metadata.content_type,
        });
      }

      const url = `${supabaseUrl}/storage/v1/render/image/public/drawings/${encodeURIComponent(
        attachment.object_id,
      )}`;

      const img = await decodeImage(url);

      return {
        attachment_id: attachment.id,
        id: attachment.object_id.split("/")[0],
        name: attachment.name,
        extension: attachment.metadata.extension,
        object_id: attachment.object_id,
        preview_object_id: attachment.object_id,
        properties: {
          width: img.width,
          height: img.height,
        },
      };
    }
  }

  async function iiifThumbnail(image) {
    if (!image.object_name || !image.location.position) return null;
    const slug = `${nameMinusExtension(image.object_name)}.${image.object_page}.tiff`;
    const path = encodeURIComponent(slug);
    const { x, y, width, height } = image.location.clip;
    return `${$triopsUrl}/iiif/2/drawings/${path}/${x},${y},${width},${height}/full/0/default.jpg`;
  }

  async function imgThumbnail(image) {
    const { data, error } = await api.storage.from("documents").download(image.object_name);
    if (error) return null;

    const url = URL.createObjectURL(data);
    const clipped = await imgClipToBase64(url, image.location.clip);
    return clipped;
  }

  async function getImageThumbnail(image) {
    if (!image) {
      thumbnail = null;
      return;
    }

    if (image.type === "clip") {
      if (image.extension === "pdf") {
        thumbnail = await iiifThumbnail(image);
      } else {
        thumbnail = await imgThumbnail(image);
      }
      return;
    }

    if (!previewable.includes(image.metadata.content_type)) {
      thumbnail = null;
      return;
    }
    const att = await api.storage.from("attachments").list("", { limit: 1, search: image.metadata.id });

    if (!att.error && att.data.length) {
      if (image.metadata.content_type === "application/pdf") {
        try {
          thumbnail = await pdfThumbnail(image);
        } catch (error) {
          console.log(error);
          thumbnail = null;
          return;
        }
        return;
      }
      const { data, error } = await api.storage.from("attachments").createSignedUrl(image.object_id, 60, {
        transform: { width: 512, height: 320, resize: "contain" },
      });

      if (error) {
        thumbnail = null;
        return;
      }

      thumbnail = data.signedUrl;
    }
  }

  async function makePreview() {
    const attachment = currentImage;
    try {
      // Check whether attachment preview has already been created
      const drawing = await ensureDrawingExists(attachment);

      if (itemid) {
        await group.updateItem(itemid, "drawing", drawing);
      } else if (typeid) {
        await group.updateType(typeid, "drawing", drawing);
      }
    } catch (error) {
      console.log("223", error);
    }
  }

  function removePreview() {
    if (itemid) {
      group.updateItem(itemid, "drawing", null);
    } else if (typeid) {
      group.updateType(typeid, "drawing", null);
    }
  }

  async function openAttachment(attachment) {
    try {
      const { data, error } = await api.storage.from("attachments").download(attachment.object_id);
      if (error) throw error;

      downloadBlob(data, attachment.name);
    } catch (error) {
      console.log("242", error);
    }
  }

  function editLink(attachment) {
    attachmentToUpdate = attachment;
    urlName = attachment.name;
    urlToUpload = attachment.url;
    updateLinkModal.open();
  }

  function updateLink() {
    group.updateAttachment(attachmentToUpdate.id, {
      name: urlName,
      url: urlToUpload,
    });
    updateLinkModal.close();
  }

  function confirmRemoveAttachment(attachment) {
    attachmentToRemove = attachment;
    removeModal.open();
  }

  async function removeAttachment(attachment) {
    if (itemid) {
      const item = $group.items[itemid];
      if (attachment.id === item?.drawing?.attachment_id) {
        await group.updateItem(itemid, "drawing", null);
      }
    } else if (typeid) {
      const type = $group.types[typeid];
      if (attachment.id === type?.drawing?.attachment_id) {
        await group.updateType(typeid, "drawing", null);
      }
    }

    await group.removeAttachment(attachment.id);
  }

  function gotoPrev() {
    currentImageIndex -= 1;
  }

  function gotoNext() {
    currentImageIndex += 1;
  }

  function setCurrentImage(attachment) {
    currentImageIndex = viewableImages.findIndex((a) => a.id === attachment.id);
  }
</script>

<div class="px-6 text-xs">
  <div class="flex justify-between items-center">
    <h3 class="mb-2">Attachments</h3>
    <div>
      <button class="prev-next-button" on:click={gotoPrev} disabled={!prevImage} class:active={prevImage}>
        <CaretLeft />
      </button>
      <button class="prev-next-button" on:click={gotoNext} disabled={!nextImage} class:active={nextImage}>
        <CaretRight />
      </button>
    </div>
  </div>
  {#if viewableImages.length > 0}
    <div class="border mb-2">
      <div class="relative">
        {#if thumbnail}
          <div class="w-full h-36 relative">
            <img class="w-full h-full object-contain text-gray-500" src={thumbnail} alt={currentImage.name} />
            {#if itemid || typeid}
              {@const record = itemid ? $group.items[itemid] : $group.types[typeid]}
              {@const isDrawing = record?.drawing?.attachment_id === currentImage.id}
              {#if isDrawing}
                <button
                  class="absolute bottom-0 right-0 px-2 py-1 flex gap-1 items-center text-xs rounded bg-gray-100/75"
                  on:click={removePreview}>
                  <div>Remove Drawing</div>
                  <ImageIcon />
                </button>
              {:else}
                <button
                  class="absolute bottom-0 right-0 px-2 py-1 flex gap-1 items-center text-xs rounded bg-gray-100/75"
                  on:click={makePreview}>
                  <div>Set Drawing</div>
                  <ImageIcon />
                </button>
              {/if}
            {/if}
          </div>
        {:else}
          <div class="w-full h-36 flex items-center justify-center">
            <div class="animate-spin">
              <SpinnerIcon />
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
  {#if attachmentList.length > 0}
    <div class="space-y-2 mb-2">
      {#each attachmentList as attachment}
        {@const isPreview = attachment.id === currentImage?.id}
        <div class="attachment flex items-center gap-2 justify-between overflow-hidden">
          <div class="flex gap-1 overflow-hidden">
            {#if attachment.type === "clip" || previewable.includes(attachment.metadata.content_type)}
              <button
                class="flex-none"
                class:text-black={isPreview}
                class:text-gray-400={!isPreview}
                on:click={() => setCurrentImage(attachment)}
                use:tooltip={{ text: "Show Image" }}>
                {#if attachment.type === "clip"}
                  <LocationItemIcon />
                {:else}
                  <svelte:component this={fileIcons[extension(attachment.name)] || FileIcon} />
                {/if}
              </button>
              <button class="grow overflow-hidden" on:click={() => setCurrentImage(attachment)}>
                <div class="truncate">
                  {attachment.name}
                </div>
              </button>
            {:else if attachment?.metadata?.content_type === "text/uri-list"}
              <div class="flex-none">
                <ShareIcon />
              </div>
              <a href={attachment.url} target="_blank" class="hover:text-blue-500">
                <div class="grow truncate">
                  {attachment.name || attachment.url}
                </div>
              </a>
            {:else}
              <div class="flex-none text-gray-400">
                <FileIcon />
              </div>
              <div class="grow truncate">
                {attachment.name}
              </div>
            {/if}
          </div>
          {#if attachment.type !== "clip"}
            <div class="flex gap-1">
              {#if attachment?.metadata?.content_type === "text/uri-list"}
                {#if !disabled}
                  <button
                    class="flex-none text-gray-400 hover:text-black"
                    on:click={() => editLink(attachment)}
                    use:tooltip={{ text: "Edit Link" }}>
                    <EditIcon />
                  </button>
                {/if}
              {:else}
                <button
                  class="flex-none text-gray-400 hover:text-black"
                  on:click={() => openAttachment(attachment)}
                  use:tooltip={{ text: "Download Attachment" }}>
                  <DownloadIcon />
                </button>
              {/if}
              {#if !disabled}
                <button
                  class="flex-none text-gray-400 hover:text-black"
                  use:tooltip={{ text: "Delete Attachment" }}
                  on:click={() => confirmRemoveAttachment(attachment)}>
                  <TrashIcon />
                </button>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
  <div class="flex items-center justify-end">
    {#if attachmentList.length === 0}
      <div class="grow text-gray-400">No attachments.</div>
    {/if}
    {#if !disabled}
      <button on:click={upload} class="flex gap-2 items-center">
        <UploadIcon />
        <div>Upload</div>
      </button>
    {/if}
  </div>
</div>

<Modal bind:this={dropzoneModal} closeable>
  <div slot="title">Upload</div>
  <div slot="content" class="space-y-2">
    {#if uploadUrl}
      <TextInput label="Name" bind:value={urlName} border inputOnKeydown />
      <TextInput label="URL" bind:value={urlToUpload} border inputOnKeydown />
      <button class="btn btn-primary w-full" disabled={!urlToUpload} on:click={uploadUrlAttachment}>
        Upload
      </button>
      <div class="text-center">
        or
        <button class="btn-text" on:click={() => (uploadUrl = false)}>Upload a File</button>
      </div>
    {:else}
      <Dropzone on:drop={uploadFiles} bind:this={dropzone} />
      <div class="text-center">
        or
        <button class="btn-text" on:click={() => (uploadUrl = true)}>Upload a URL</button>
      </div>
    {/if}
  </div>
</Modal>

<Modal bind:this={updateLinkModal} closeable>
  <div slot="title">Edit Link</div>
  <div slot="content" class="space-y-2">
    <TextInput label="Name" bind:value={urlName} border inputOnKeydown />
    <TextInput label="URL" bind:value={urlToUpload} border inputOnKeydown />
    <button class="btn btn-primary w-full" on:click={updateLink}> Update Link </button>
  </div>
</Modal>

<Modal
  closeable
  on:confirm={() => removeAttachment(attachmentToRemove)}
  bind:this={removeModal}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}>
  <div slot="title">Delete Attachment</div>
  <div slot="content" class="space-y-4 text-sm">
    <div>Are you sure you want to delete this attachment?</div>
    <div class="font-bold">
      {attachmentToRemove.name || attachmentToRemove.url || "Attachment"}
    </div>
  </div>
</Modal>

<style lang="scss">
  .prev-next-button {
    @apply rounded p-1 text-gray-400;

    &.active {
      @apply text-black;
      &:hover {
        @apply bg-gray-100;
      }
    }
  }
</style>
